import {
  createAsyncThunk,
  ActionReducerMapBuilder,
  PayloadAction,
  AsyncThunk,
} from '@reduxjs/toolkit';
import { Draft } from 'immer';

// Generic data state interface
export interface DataState<T> {
  data: T | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

// Generic async thunk creator
export const createDataAsyncThunk = <TData, TParam = void>(
  typePrefix: string,
  serviceFunction: (param: TParam) => Promise<TData>
) => {
  return createAsyncThunk<TData, TParam>(typePrefix, async (param: TParam, { rejectWithValue }) => {
    try {
      return await serviceFunction(param);
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  });
};

export interface DataStateWithParams<T, TParam = any> extends DataState<T> {
  lastParams?: TParam;
}

// Generic reducer case builder
export const addDataAsyncCases = <TState, TData, TParam = void>(
  builder: ActionReducerMapBuilder<TState>,
  asyncThunk: ReturnType<typeof createDataAsyncThunk<TData, TParam>>,
  stateSelector: (state: Draft<TState>) => Draft<DataState<TData>>
) => {
  builder
    .addCase(asyncThunk.pending, state => {
      const dataState = stateSelector(state);
      dataState.status = 'loading';
      dataState.error = null;
    })
    .addCase(asyncThunk.fulfilled, (state, action: PayloadAction<TData>) => {
      const dataState = stateSelector(state);
      dataState.status = 'succeeded';
      dataState.data = action.payload as Draft<TData>;
      dataState.error = null;
    })
    .addCase(asyncThunk.rejected, (state, action) => {
      const dataState = stateSelector(state);
      dataState.status = 'failed';
      dataState.error = action.payload as string;
    });
};

// Enhanced reducer case builder with parameter tracking
export const addDataAsyncCasesWithParams = <TState, TData, TParam = void>(
  builder: ActionReducerMapBuilder<TState>,
  asyncThunk: AsyncThunk<TData, TParam, any>,
  stateSelector: (state: Draft<TState>) => Draft<DataStateWithParams<TData, TParam>>
) => {
  builder
    .addCase(asyncThunk.pending, state => {
      const dataState = stateSelector(state);
      dataState.status = 'loading';
      dataState.error = null;
    })
    .addCase(asyncThunk.fulfilled, (state, action) => {
      const dataState = stateSelector(state);
      dataState.status = 'succeeded';
      dataState.data = action.payload as Draft<TData>;
      dataState.error = null;
      dataState.lastParams = action.meta.arg as Draft<TParam>; // Store parameters
    })
    .addCase(asyncThunk.rejected, (state, action) => {
      const dataState = stateSelector(state);
      dataState.status = 'failed';
      dataState.error = action.payload as string;
      // Track params even on failure so caching logic can avoid infinite refetch loops
      dataState.lastParams = action.meta.arg as Draft<TParam>;
    });
};
